import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, todayStr, fmtDate } from "@/lib/format";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileBarChart, Download } from "lucide-react";

export default function Reports() {
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [driver, setDriver] = useState("all");
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/drivers").then((r) => setDrivers(r.data)); }, []);

  const run = () => {
    setLoading(true);
    let url = `/reports/summary?date_from=${from}&date_to=${to}`;
    if (driver !== "all") url += `&driver_id=${driver}`;
    api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(run, []);

  const exportCsv = () => {
    if (!data) return;
    const head = ["Date", "Type", "Consumer/Customer", "Cylinder", "Qty", "Amount", "Mode", "Allocated"];
    const lines = data.transactions.map((t) => [t.date, t.type, t.consumer_no || t.customer_name || "", t.cylinder_label, t.qty, t.amount, t.payment_mode, t.allocated_consumer_no || ""].join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `anila_report_${from}_${to}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2"><FileBarChart className="h-6 w-6 text-primary" /> Reports</h1>
        <p className="text-sm text-muted-foreground">Collections and sales for any date range.</p>
      </div>

      <Card className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="flex flex-col gap-1.5"><Label>From</Label><DateField value={from} onChange={setFrom} testid="reports-from-input" className="w-full" /></div>
          <div className="flex flex-col gap-1.5"><Label>To</Label><DateField value={to} onChange={setTo} testid="reports-to-input" className="w-full" /></div>
          <div><Label>Driver</Label>
            <Select value={driver} onValueChange={setDriver}>
              <SelectTrigger data-testid="reports-driver-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={run} data-testid="reports-run-button">Generate</Button>
          <Button variant="outline" onClick={exportCsv} disabled={!data} data-testid="reports-export-csv-button"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        </div>
      </Card>

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4"><div className="text-xs text-muted-foreground">Collection</div><div className="text-2xl font-semibold tabular-nums">{inr(data.collection_total)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Cash / UPI</div><div className="text-lg font-semibold tabular-nums">{inr(data.cash_total)} / {inr(data.upi_total)}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Transactions</div><div className="text-2xl font-semibold tabular-nums">{data.count}</div></Card>
          <Card className="p-4"><div className="text-xs text-muted-foreground">Non-consumer sales</div><div className="text-2xl font-semibold tabular-nums">{data.nonconsumer_count} <span className="text-sm text-muted-foreground">({inr(data.nonconsumer_amount)})</span></div></Card>
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Customer</TableHead><TableHead>Cylinder</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Mode</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && (!data || data.transactions.length === 0) && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No transactions.</TableCell></TableRow>}
              {!loading && data && data.transactions.slice(0, 200).map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{fmtDate(t.date)}</TableCell>
                  <TableCell><span className={`text-xs ${t.type === "nonconsumer_sale" ? "text-amber-700" : "text-muted-foreground"}`}>{t.type === "nonconsumer_sale" ? "Non-consumer" : "Consumer"}</span></TableCell>
                  <TableCell className="font-mono text-sm">{t.consumer_no || t.customer_name || "—"}</TableCell>
                  <TableCell>{t.cylinder_label}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(t.amount)}</TableCell>
                  <TableCell className="uppercase text-xs">{t.payment_mode}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
